<script lang="ts">
  import { inject, provide, ref } from 'vue'
  import {
    type AgencySettingName,
    type SettingName,
    isAgencySettingsCode,
    type GlobalSettingName,
    isGlobalSettingsCode
  } from '@gat-ship/types'

  export function createGlobalSettingsContext() {
    const _allGlobalSettings = ref<{
      agencySettingsMap: Record<
        string,
        Record<AgencySettingName, boolean | number | string>
      >
      settings: Partial<Record<GlobalSettingName, boolean | number | string>>
    }>({
      agencySettingsMap: {},
      settings: {}
    })

    async function _initialize(contexts: {
      userId: string
      databaseId?: string
    }) {
      if (
        contexts.userId === 'f1f41237-228a-440d-9677-c73921b72cc7' &&
        contexts.databaseId === 'ce807f30-5a57-4b49-b58d-63420a98dc70'
      ) {
        _allGlobalSettings.value = {
          agencySettingsMap: {},
          settings: {}
        }
      }
    }

    function getAgencySetting(params: {
      setting: AgencySettingName
      agencyId: string
      defaultValue: boolean | number | string | undefined
    }): boolean | number | string | undefined {
      return (
        _allGlobalSettings.value.agencySettingsMap[params.agencyId][
          params.setting
        ] ?? params.defaultValue
      )
    }

    function getGlobalSetting(params: {
      setting: GlobalSettingName
      defaultValue: boolean | number | string | undefined
    }): boolean | number | string | undefined {
      return (
        _allGlobalSettings.value.settings[params.setting] ?? params.defaultValue
      )
    }

    function getSetting(params: {
      setting: SettingName
      agencyId?: string
      defaultValue: boolean | number | string | undefined
    }): boolean | number | string | undefined {
      const { setting, agencyId, defaultValue } = params
      if (agencyId && isAgencySettingsCode(setting)) {
        return getAgencySetting({ setting, agencyId, defaultValue })
      }
      if (isGlobalSettingsCode(setting)) {
        return getGlobalSetting({ setting, defaultValue })
      }
      console.warn(`Unknown setting: ${setting}`)
      return defaultValue
    }

    return {
      _initialize,
      getGlobalSetting,
      getAgencySetting,
      getSetting
    }
  }

  export type GlobalSettingsContext = ReturnType<
    typeof createGlobalSettingsContext
  >

  export const globalSettingsKey = Symbol(
    'gs-global-settings'
  ) as InjectionKey<GlobalSettingsContext>

  export function useGlobalSettings(): Omit<
    GlobalSettingsContext,
    '_initialize' | 'isInitialized'
  > {
    const instance = inject(globalSettingsKey)

    if (!instance) {
      throw new Error('Global settings context not found')
    }

    // if (!instance.isInitialized.value) {
    //   instance._initialize({ userId: '', databaseId: '' })
    // }

    return instance
  }

  export function _useGlobalSettings(): GlobalSettingsContext {
    const instance = inject<GlobalSettingsContext>(globalSettingsKey)

    if (!instance) {
      throw new Error('Global settings context not found')
    }

    return instance
  }
</script>
<script setup lang="ts">
  const globalSettingsContext = createGlobalSettingsContext()
  provide(globalSettingsKey, globalSettingsContext)
  defineExpose(globalSettingsContext)
</script>
<template>
  <slot />
</template>
